import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Monitor, Smartphone, Tablet, Globe } from 'lucide-react';
import { DeviceData, BrowserData } from '../types';

// Import browser logo images
import chromeLogo from '@/assets/images/chrome.png';
import firefoxLogo from '@/assets/images/firefox.png';
import safariLogo from '@/assets/images/safari.png';
import edgeLogo from '@/assets/images/microsoft.png';
import operaLogo from '@/assets/images/opera.png';
import samsungLogo from '@/assets/images/samsung.png';

interface DevicesBrowsersWidgetProps {
  timePeriod?: string;
  devices: DeviceData[];
  browsers: BrowserData[];
}

const DevicesBrowsersWidget: React.FC<DevicesBrowsersWidgetProps> = ({ 
  timePeriod = '7d',
  devices,
  browsers
}) => {
  const getDeviceIcon = (deviceName: string) => {
    switch (deviceName.toLowerCase()) {
      case 'desktop':
        return <Monitor className="h-4 w-4" />;
      case 'mobile':
        return <Smartphone className="h-4 w-4" />;
      case 'tablet':
        return <Tablet className="h-4 w-4" />;
      default:
        return <Monitor className="h-4 w-4" />;
    }
  };

  const getBrowserIcon = (browserName: string) => {
    switch (browserName.toLowerCase()) {
      case 'chrome':
        return <img src={chromeLogo} alt="Chrome" className="h-4 w-4" />;
      case 'safari':
        return <img src={safariLogo} alt="Safari" className="h-4 w-4" />;
      case 'firefox':
        return <img src={firefoxLogo} alt="Firefox" className="h-4 w-4" />;
      case 'edge':
        return <img src={edgeLogo} alt="Edge" className="h-4 w-4" />;
      case 'opera':
        return <img src={operaLogo} alt="Opera" className="h-4 w-4" />;
      case 'samsung internet':
        return <img src={samsungLogo} alt="Samsung Internet" className="h-4 w-4" />;
      case '(not set)':
        return <Globe className="h-4 w-4" />;
      default:
        return <Globe className="h-4 w-4" />;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-0">
        <CardTitle className="text-sm font-medium">Devices & Browsers</CardTitle>
        <Globe className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="px-0">
        <div className="space-y-4">
          {/* Devices Section */}
          <div className="space-y-2 px-0">
            <h4 className="text-xs font-medium text-muted-foreground">Devices</h4>
            {devices.map((device, index) => (
              <div key={device.device} className="group relative">
                <div className="relative flex items-center justify-between p-2 rounded-lg overflow-hidden">
                  {/* Background Progress Bar */}
                  <div 
                    className="absolute inset-0 bg-primary/10 transition-all duration-300 ease-out "
                    style={{ width: `${device.percentage}%` }}
                  ></div>
                  
                  {/* Content on top of progress bar */}
                  <div className="relative flex items-center space-x-3 flex-1">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                      {getDeviceIcon(device.device)}
                    </div>

                    <div className="relative">
                      <span className="text-sm font-semibold text-foreground text-sm text-muted-foreground text-foreground">
                        {device.percentage.toFixed(1)}%
                      </span>
                    </div>
                    <span className="text-sm text-muted-foreground text-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {device.device}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Browsers Section */}
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground">Browsers</h4>
            {browsers.map((browser, index) => (
              <div key={browser.browser} className="group relative">
                <div className="relative flex items-center justify-between p-2 rounded-lg overflow-hidden ">
                  {/* Background Progress Bar */}
                  <div 
                    className=" absolute inset-0 bg-primary/10 transition-all duration-300 ease-out"
                    style={{ width: `${browser.percentage}%` }}
                  ></div>
                  
                  {/* Content on top of progress bar */}
                  <div className="relative flex items-center space-x-3 flex-1">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-sm text-muted-foreground text-foreground">
                      {getBrowserIcon(browser.browser)}
                    </div>

                    <div className="relative">
                      <span className="text-sm text-muted-foreground text-foreground">
                        {browser.percentage.toFixed(1)}%
                      </span>
                    </div>
                    <span className="text-sm font-medium text-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {browser.browser}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DevicesBrowsersWidget;
